<template>
  <div class="relative flex flex-1 flex-col">
    <label
      v-if="label"
      :for="inputId"
      :class="
        twMerge(
          'mb-1.5 inline-block font-sans text-base text-black lg:mb-3 lg:text-lg lg:leading-snug',
          styleLabel,
        )
      "
    >
      {{ label }}
      <span v-if="required">*</span>
    </label>
    <div :class="twMerge('relative', styleInputWrapper)">
      <div class="relative flex items-center">
        <input
          :id="inputId"
          ref="inputEl"
          v-model="numericInput"
          :type="inputType"
          :inputmode="inputMode"
          :class="
            twMerge(
              'max-h-[60px] flex-1 rounded-md border-transparent py-4 pl-3 pr-10 leading-none outline-0 focus:border-transparent focus:ring-0 disabled:border-[#ADBACB] disabled:bg-[#D8D8D8] lg:p-4 lg:text-xl lg:leading-none',
              styleInput,
              v$?.$invalid && v$?.$dirty && 'border-pink focus:border-pink',
            )
          "
          :placeholder="placeholder"
          :required="required"
          :min="min"
          :max="max"
          :name="name"
          :hidden="hidden"
          :disabled="disabled"
          :readonly="readOnly"
          @input="e => update(e, name)"
          @blur="v$ && v$.$validate()"
          @focus="emit('focus')"
        />
        <div
          v-if="right"
          class="pointer-events-none absolute right-2 top-0 flex h-full items-center"
          v-html="right"
        ></div>
        <slot name="right"></slot>
      </div>
      <div class="relative pb-3">
        <div
          v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
          :class="[
            styleError,
            'absolute left-0 right-0 mt-0.5 px-1 py-0 pr-3 font-sans text-sm lg:text-base',
            useDarkMode ? 'text-pink-light' : 'text-pink',
          ]"
          role="alert"
        >
          <span v-for="(err, i) in v$?.$errors" :key="err.$uid">{{ (i as number) > 0 ? '. ' : '' }}{{ err.$message }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
  inject,
  computed,
  useId,
  type Ref,
} from 'vue'
import { twMerge } from 'tailwind-merge'

const props = defineProps({
  label: String,
  styleInput: String,
  styleInputWrapper: String,
  styleLabel: String,
  styleError: String,
  name: { type: String, default: '' },
  type: {
    type: String,
    default: 'text',
  },
  required: { type: Boolean, default: false },
  placeholder: String,
  modelValue: [String, Number],
  value: [String, Number],
  modelModifiers: {
    type: Object,
    default: () => ({}),
  },
  right: String,
  min: Number,
  max: Number,
  disabled: { type: Boolean, default: false },
  hidden: { type: Boolean, default: false },
  readOnly: { type: Boolean, default: false },
  v$: Object,
})

// An `id` passed by the caller falls through to the root <div>, not to the <input>,
// so the input needs its own id for the <label for> to resolve. useId() is stable
// across SSR and hydration, and unique when the same field appears twice on a page.
const inputId = useId()

// The <input> is wrapped, so a parent holding a ref to this component can't reach
// it to move focus. Expose focus() instead of leaking the element.
const inputEl = ref<HTMLInputElement | null>(null)
defineExpose({ focus: () => inputEl.value?.focus() })

// `type` carries two vocabularies: HTML input types from direct callers, and
// Drupal element names from the webform path (`inputFields` in refresh-common is
// tel, email, textfield, date). Anything not on this list renders as text, so a
// Drupal name can never reach the DOM as <input type="textfield">.
//
// The list is an allowlist, not the set of valid HTML types. `date` is a real
// input type and is left off on purpose: passing it through would swap those
// fields for a native date picker, which is a visual change that wants its own
// review rather than arriving as a side effect of this one.
const PASSTHROUGH_TYPES = ['text', 'email', 'search', 'tel', 'url', 'password']

const inputType = computed(() => (PASSTHROUGH_TYPES.includes(props.type) ? props.type : 'text'))

// `number` deliberately stays a text input. Every caller passing it is a
// donation or membership amount field, and a native number input changes its
// value when the wheel scrolls over it — a donor scrolling the page could alter
// their own amount without noticing. inputmode gets the numeric keypad on
// mobile without that hazard, and keeps the digit stripping in update() working.
const inputMode = computed(() => (props.type === 'number' ? 'numeric' : undefined))

const numericInput = ref(props.modelValue ?? props.value)

const emit = defineEmits(['update:modelValue', 'setData', 'focus'])

const settings = inject<Ref<string[]>>('settings', ref([]))
const useDarkMode = computed(() => settings.value?.includes('dark_theme') ?? false)

const stopWatcher = watch(
  () => props.modelValue ?? props.value,
  val => {
    numericInput.value = val
  },
)

const update = (e: Event | null, key: string | number) => {
  if (props.modelModifiers.trim === true && typeof numericInput.value === 'string') {
    numericInput.value = numericInput.value.trim()
  }
  // Note: Is this really necessary?
  if (props.type === 'number' && typeof String(numericInput?.value)?.replace === 'function') {
    numericInput.value = Number(String(numericInput.value).replace(/[^0-9]/g, ''))
  }
  emit('setData', { key, value: numericInput.value })
  emit('update:modelValue', numericInput.value)
}

onMounted(() => {
  nextTick(() => {
    update(null, props.name)
  })
})

onBeforeUnmount(() => {
  stopWatcher()
})
</script>
